import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FadeDivider, OptionalChip, RequiredChip, RHFPhoneInput, RHFTextField } from '@onesource/components';
import { TCity, TCountry, TState } from '@onesource/schemas';
import { FC } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdFacebook } from 'react-icons/md';
import { SiInstagram, SiLinkedin, SiTiktok, SiX, SiYoutube } from 'react-icons/si';

import { TProFormValues } from './steps';

type TProfileSocials = {
  facebook: string;
  instagram: string;
  youtube: string;
  twitter: string;
  linkedin: string;
  tiktok: string;
};

type Props = {
  isPending: boolean;
  countries: TCountry[];
  states: TState[];
  cities: TCity[];
  profilePhone: string;
  profileSocials: TProfileSocials;
  copyPhone: boolean;
  copySocials: boolean;
  disableCopyPhone: boolean;
  disableCopySocials: boolean;
  onCopyPhoneChange: (checked: boolean) => void;
  onCopySocialsChange: (checked: boolean) => void;
};

export const ProDirectoryStepBusiness: FC<Props> = ({
  isPending,
  countries,
  states,
  cities,
  profilePhone,
  profileSocials,
  copyPhone,
  copySocials,
  disableCopyPhone,
  disableCopySocials,
  onCopyPhoneChange,
  onCopySocialsChange,
}) => {
  const { control, setValue } = useFormContext<TProFormValues>();
  const watchedCountryId = useWatch({ control, name: 'businessCountryId' });
  const watchedStateId = useWatch({ control, name: 'businessStateId' });

  return (
    <Grid container spacing={3}>
      {/* Business name / email */}
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Business Name <OptionalChip />
        </Typography>
        <RHFTextField
          name="companyName"
          placeholder="Business name"
          helperText="Please add only the name of your business"
          disabled={isPending}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Business Email <OptionalChip />
        </Typography>
        <RHFTextField
          name="companyEmail"
          placeholder="Business email"
          helperText="This email will be on your Pro Directory page"
          disabled={isPending}
        />
      </Grid>

      {/* Business website / phone */}
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Business Website <OptionalChip />
        </Typography>
        <RHFTextField
          name="professionalWebsite"
          placeholder="example.com"
          helperText="Add a link to your website"
          disabled={isPending}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            Phone <OptionalChip />
          </Typography>
          <Tooltip title={!profilePhone ? 'No phone in your account profile' : ''}>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ my: '-9px', marginTop: '-9px' }}
                  checked={copyPhone}
                  disabled={disableCopyPhone || isPending}
                  onChange={e => onCopyPhoneChange(e.target.checked)}
                />
              }
              label={<Typography variant="caption">Copy from My Account</Typography>}
            />
          </Tooltip>
        </Stack>
        <RHFPhoneInput
          name="phone"
          placeholder="Phone Number"
          helperText="This Phone number will appear on your Pro Directory page"
          disabled={isPending || copyPhone}
        />
      </Grid>

      {/* Business address street */}
      <Grid item xs={12}>
        <FadeDivider />
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Business Address <OptionalChip />
        </Typography>
        <RHFTextField
          name="businessStreet"
          placeholder="Neighbourhood / Street address"
          helperText="Here you can add your Business Address, if valid"
          disabled={isPending}
        />
      </Grid>

      {/* Country / State / City */}
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Country <RequiredChip />
        </Typography>
        <Controller
          name="businessCountryId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={countries}
              getOptionLabel={o => o.name}
              value={countries.find(c => c.id === field.value) ?? null}
              onChange={(_, option) => {
                field.onChange(option?.id ?? null);
                setValue('businessStateId', null);
                setValue('businessCityId', null);
              }}
              disabled={isPending}
              renderOption={(props, option) => (
                // oxlint-disable-next-line react/jsx-key
                <li {...props} key={option.id}>
                  {option.flag && <span style={{ marginRight: 8 }}>{option.flag}</span>}
                  {option.name}
                </li>
              )}
              renderInput={params => {
                const selected = countries.find(c => c.id === field.value);
                return (
                  <TextField
                    {...params}
                    placeholder="Select your Country"
                    helperText="Select your Country location. If your country is missing, please contact support"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selected?.flag ? (
                        <>
                          <span style={{ marginRight: 4, fontSize: 18, lineHeight: 1 }}>{selected.flag}</span>
                          {params.InputProps.startAdornment}
                        </>
                      ) : (
                        params.InputProps.startAdornment
                      ),
                    }}
                  />
                );
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          State / Region <RequiredChip />
        </Typography>
        <Controller
          name="businessStateId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={states}
              getOptionLabel={o => o.name}
              value={states.find(s => s.id === field.value) ?? null}
              onChange={(_, option) => {
                field.onChange(option?.id ?? null);
                setValue('businessCityId', null);
              }}
              disabled={isPending || !watchedCountryId}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select your State / Region"
                  helperText="Select your State / Region location. If your State / Region is missing, please contact support"
                />
              )}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          City <RequiredChip />
        </Typography>
        <Controller
          name="businessCityId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={cities}
              getOptionLabel={o => o.name}
              value={cities.find(c => c.id === field.value) ?? null}
              onChange={(_, option) => field.onChange(option?.id ?? null)}
              disabled={isPending || !watchedStateId}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select your City"
                  helperText="Select your City location. If your City is missing, please contact support"
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Social Networks */}
      <Grid item xs={12}>
        <FadeDivider />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Social networks
          </Typography>
          <Tooltip
            title={!Object.values(profileSocials).some(Boolean) ? 'No social links in your account profile' : ''}
          >
            <span>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={copySocials}
                    disabled={disableCopySocials || isPending}
                    onChange={e => onCopySocialsChange(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Copy from My Account</Typography>}
              />
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          All optional.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Facebook <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.facebook"
              placeholder="https://facebook.com/yourprofile"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <MdFacebook size={20} style={{ marginRight: 8, color: '#1877F2', flexShrink: 0 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Instagram <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.instagram"
              placeholder="https://instagram.com/yourprofile"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <SiInstagram size={20} style={{ marginRight: 8, color: '#E1306C', flexShrink: 0 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Youtube <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.youtube"
              placeholder="https://youtube.com/@yourchannel"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <SiYoutube size={20} style={{ marginRight: 8, color: '#FF0000', flexShrink: 0 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              X <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.twitter"
              placeholder="https://x.com/yourhandle"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <SiX size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Linkedin <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.linkedin"
              placeholder="https://linkedin.com/in/yourprofile"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <SiLinkedin size={20} style={{ marginRight: 8, color: '#0A66C2', flexShrink: 0 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Tik Tok <OptionalChip />
            </Typography>
            <RHFTextField
              name="socials.tiktok"
              placeholder="https://tiktok.com/@yourhandle"
              disabled={isPending || copySocials}
              InputProps={{
                startAdornment: <SiTiktok size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
